import { Dot } from "@/components/Dot";
import { PlusPlus } from "@/components/molecules/PlusPlus/PlusPlus";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import Card from "@/design-system/Card/Card";
import { PlayerCard as FullPlayerCard } from "@/design-system/PlayerCard/PlayerCardView";
import { usePriceDisplay } from "@/design-system/Price/usePriceDisplay";
import { MetaRatingChip } from "@/design-system/Tags/MetaRatingChip/MetaRatingChip";
import { NewTag } from "@/design-system/Tags/NewTag/NewTag";
import { convertSBCPrice } from "@/functions/convertPlayerPrice";
import { cn } from "@/lib/utils";
import { getAttributeColor } from "@/pages/DetailedPlayer/sections/Attributes/AttributesSection";
import { EvoDataRow } from "@/pages/EvolutionsHub/EvoDataRow";
import { EvoChainFromIds } from "@/pages/EvolutionsHub/sections/EvoChainFromIds";
import {
  contentLabelsSelector,
  eventTokensSelector,
  getVersionAssetsSelector,
} from "@/redux/loadedContent/loadedContentSlice";
import { useTranslation } from "@repo/shared/i18n";
import {
  BasePlayer,
  EventToken,
  ObjectiveDisplay,
  PriceInfo,
} from "@repo/shared/types";
import { FACE_STAT_KEYS } from "@repo/shared/utils";
import { Star } from "lucide-react";
import { useSelector } from "react-redux";

import {
  owned_icon_true as ownedIcon,
  untradeableOnly as untradeableIcon,
} from "../../../../icons";
import type { PlayersCardViewPropsExpanded } from "./PlayersCardView";
import {
  CompareToggleButton,
  cardCompareOrigin,
  useCompareToggle,
} from "./PlayersCardView";
import { EvoPopularity } from "./Sections/EvoPopularity";
import { PlayStyles } from "./Sections/PlayStyles";

const FaceStats = ({
  attributes,
  upgrades,
  isGk,
}: {
  attributes: number[];
  upgrades?: number[];
  isGk: boolean;
}) => {
  const { playerAttributes } = useSelector(contentLabelsSelector);
  const keys = isGk ? FACE_STAT_KEYS.gk : FACE_STAT_KEYS.outfield;
  return (
    <div className="grid grid-cols-6 gap-x-3">
      {keys.map((key, i) => (
        <div key={key} className="flex w-8 flex-col items-center gap-y-0.5">
          <span
            className={cn(
              "text-[11px] font-medium uppercase",
              upgrades?.[i] > 0 ? "text-upgrade" : "text-foreground-muted",
            )}
          >
            {playerAttributes[key]?.short ?? key}
          </span>
          <span className="text-base font-semibold text-foreground">
            {attributes[i]}
          </span>
          <span
            className={cn(
              "mt-0.5 h-[3px] w-6 rounded-full",
              getAttributeColor(attributes[i]),
            )}
          />
        </div>
      ))}
    </div>
  );
};

const Stars = ({
  label,
  value,
  upgraded,
}: {
  label: string;
  value: number;
  upgraded?: boolean;
}) => (
  <div className="flex flex-col items-center gap-y-0.5">
    <span
      className={cn(
        "text-[11px] font-medium uppercase",
        upgraded ? "text-upgrade" : "text-foreground-muted",
      )}
    >
      {label}
    </span>
    <span className="flex items-center gap-x-0.5 text-base font-semibold text-foreground">
      {value}
      <Star className="size-3.5 fill-current" />
    </span>
    {/* Same slot as the stat bars, so both rows of text line up. */}
    <span className="mt-0.5 h-[3px]" />
  </div>
);

/** Compact price ("1.2M"), semibold Oswald. */
const RowPrice = ({
  priceInfo,
  objectiveDisplay,
  eventTokens,
}: {
  priceInfo?: PriceInfo;
  objectiveDisplay?: ObjectiveDisplay;
  eventTokens?: EventToken[];
}) => {
  const { t } = useTranslation("common");
  const { iconUrl, label, price, showPrice } = usePriceDisplay({
    priceInfo,
    objectiveDisplay,
    eventTokens,
    assetsBaseUrl: import.meta.env.VITE_APP_ASSETS_BASE_URL,
  });
  return (
    <div className="flex items-center gap-x-1.5">
      {iconUrl && (
        <img className="size-5 shrink-0" src={iconUrl} alt={t("alt.coin")} />
      )}
      {label && <span className="text-sm">{label}</span>}
      {showPrice && (
        <span className="font-oswald text-base font-semibold text-foreground">
          {convertSBCPrice(price)}
        </span>
      )}
    </div>
  );
};

const Positions = ({ positions }: { positions: string[] }) => {
  const { positions: labels } = useSelector(contentLabelsSelector);
  return (
    <div className="flex flex-wrap gap-1">
      {positions.map((p) => (
        <span
          key={p}
          className="rounded-md bg-surface-raised px-1.5 py-0.5 text-[11px] font-semibold text-foreground-body"
        >
          {labels[p]?.name ?? p}
        </span>
      ))}
    </div>
  );
};

const NameLine = ({
  name,
  owned,
  untradeable,
  newPlayer,
}: {
  name: string;
  owned: boolean;
  untradeable: boolean;
  newPlayer: boolean;
}) => (
  <div className="flex min-w-0 items-center gap-x-1.5">
    <h3
      className="truncate text-base font-semibold text-foreground"
      title={name}
    >
      {name}
    </h3>
    {owned && <div className="w-4 shrink-0">{ownedIcon}</div>}
    {untradeable && <div className="w-4 shrink-0">{untradeableIcon}</div>}
    {newPlayer && <NewTag />}
  </div>
);

const RoleLine = ({
  role,
  plus,
  plusPlus,
}: {
  role: string;
  plus: boolean;
  plusPlus: boolean;
}) => (
  <div className="flex items-center gap-x-1 text-xs text-foreground-muted">
    <span className="truncate">{role}</span>
    <PlusPlus plus={plus} plusPlus={plusPlus} />
  </div>
);

/** The /players list row on the 2.0 design system. */
export const PlayerRow = (
  props: PlayersCardViewPropsExpanded & { isTouchDevice: boolean },
) => {
  const { t } = useTranslation("evoBuilder");
  const {
    isTouchDevice,
    name,
    preferredPosition,
    priceInfo,
    objectiveDisplay,
    newPlayer,
    owned,
    untradeable,
    positions,
    attributes,
    skillMoves,
    weakFoot,
    playStyles,
    playStylesPlus,
    evolutionBonuses,
    evolutionPath,
    pathwayPath,
    evoPathId,
    evolutionData,
    evoPopularity,
    metaRating,
    metaRatingDelta,
    tier,
    chemistry,
    basePlayerTierAndMetaRating,
    isRolesPlusOrPlusPlus,
    playerRoleName,
  } = props;
  const isEvoPlayer = evolutionPath?.length > 0 || !!evoPathId;
  const { compareMode, isInCompare, handleToggleCompare } = useCompareToggle(
    props,
    cardCompareOrigin(props),
  );

  const versionAssets = useSelector(getVersionAssetsSelector);
  const contentLabels = useSelector(contentLabelsSelector);
  const eventTokens = useSelector(eventTokensSelector);

  const card = (
    <div className="relative -mb-2 -mt-3 -ml-2 -mr-1 shrink-0">
      <FullPlayerCard
        scale={isTouchDevice ? 0.6 : 0.58}
        hidePrice
        isEvolution={isEvoPlayer}
        position={preferredPosition}
        player={props as unknown as BasePlayer}
        versionAssets={versionAssets}
        contentLabels={contentLabels}
        eventTokens={eventTokens}
        assetsBaseUrl={import.meta.env.VITE_APP_ASSETS_BASE_URL}
      />
      {compareMode && (
        <CompareToggleButton
          isInCompare={isInCompare}
          onToggle={handleToggleCompare}
        />
      )}
    </div>
  );
  const chip = (
    <MetaRatingChip
      metaRating={metaRating}
      metaRatingDelta={isEvoPlayer ? metaRatingDelta : undefined}
      tier={!isEvoPlayer || chemistry !== 3 ? tier : undefined}
      basePlayerTier={
        isEvoPlayer ? basePlayerTierAndMetaRating?.tier : undefined
      }
    />
  );
  const role = (
    <RoleLine
      role={playerRoleName}
      plus={isRolesPlusOrPlusPlus === "plus"}
      plusPlus={isRolesPlusOrPlusPlus === "plusPlus"}
    />
  );
  const price = (
    <RowPrice
      priceInfo={priceInfo}
      objectiveDisplay={objectiveDisplay}
      eventTokens={eventTokens}
    />
  );
  const hasPlayStyles = playStyles?.length > 0 || playStylesPlus?.length > 0;
  // Wraps instead of scrolling: the section scrolls sideways by default.
  const playStylesRow = hasPlayStyles && (
    <div className="[&_img]:size-6 [&_section]:flex-wrap [&_section]:gap-1.5 [&_section]:overflow-visible">
      <PlayStyles
        playStyles={playStyles}
        playStylesPlus={playStylesPlus}
        playStylesBonus={evolutionBonuses?.playStyles}
        playStylesPlusBonus={evolutionBonuses?.playStylesPlusBonus}
      />
    </div>
  );
  const popularity = evoPopularity ? (
    <EvoPopularity evoPopularity={evoPopularity} />
  ) : null;
  const evoRow = isEvoPlayer &&
    (evolutionData || evolutionPath?.length > 0) && (
      // Not a link: releasing a scrollbar drag would open the player.
      <div
        className="flex cursor-default items-center gap-x-2 text-foreground-body"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        {evolutionData && (
          <div className="shrink-0">
            <EvoDataRow
              evolutionData={evolutionData}
              size="medium"
              variant="inherit"
              labelGames
              className="text-xs"
            />
          </div>
        )}
        {evolutionData && evolutionPath?.length > 0 && (
          <Dot color="bg-dark-disabled" />
        )}
        {evolutionPath?.length > 0 && (
          <ScrollArea className="min-w-0 flex-1" enableShadows>
            <div className="flex w-max items-center">
              <EvoChainFromIds
                evolutionIds={evolutionPath}
                pathwayPath={pathwayPath}
              />
            </div>
            <ScrollBar orientation="horizontal" className="h-1.5" />
          </ScrollArea>
        )}
      </div>
    );

  if (isTouchDevice) {
    return (
      <Card className="relative flex w-full flex-col gap-y-2 p-3">
        <div className="flex gap-x-3">
          {card}
          {/* No name (the card art has it). Price, positions and PlayStyles
              stack with one even gap; rating and role sit top right. */}
          <div className="flex min-w-0 flex-1 flex-col justify-center gap-y-2">
            {/* A wide chip drops under the price instead of covering it. */}
            <div className="flex flex-wrap justify-between gap-x-2 gap-y-1">
              <div className="flex shrink-0 flex-col gap-y-2">
                {popularity}
                <div className="flex h-7 items-center gap-x-1.5">
                  {price}
                  {owned && <div className="w-4 shrink-0">{ownedIcon}</div>}
                  {untradeable && (
                    <div className="w-4 shrink-0">{untradeableIcon}</div>
                  )}
                  {newPlayer && <NewTag />}
                </div>
              </div>
              <div className="ml-auto flex shrink-0 flex-col items-end gap-y-1">
                {chip}
                {role}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <Positions positions={positions} />
              <div className="flex items-center gap-x-3">
                <span className="flex items-center gap-x-0.5 text-xs text-foreground-body">
                  {skillMoves}
                  <Star className="size-3 fill-current" />
                  <span className="text-foreground-muted">{t("chips.sm")}</span>
                </span>
                <span className="flex items-center gap-x-0.5 text-xs text-foreground-body">
                  {weakFoot}
                  <Star className="size-3 fill-current" />
                  <span className="text-foreground-muted">{t("chips.wf")}</span>
                </span>
              </div>
            </div>
            {playStylesRow}
          </div>
        </div>
        {evoRow}
      </Card>
    );
  }

  return (
    <Card className="relative flex w-full items-center gap-x-6 px-4 py-2">
      {card}
      {/* The evo row sits under the stats, beside the card, not below it. */}
      <div className="flex min-w-0 flex-1 flex-col gap-y-2">
        <div className="flex items-center gap-x-6">
          <div className="flex min-w-0 flex-1 flex-col gap-y-1.5">
            {popularity}
            <NameLine
              name={name}
              owned={owned}
              untradeable={untradeable}
              newPlayer={newPlayer}
            />
            <Positions positions={positions} />
            <div className="large-desktop:hidden">{playStylesRow}</div>
          </div>
          {/* Large desktop: PlayStyles in their own column, starting at the
            same x on every row; wraps to a second line when out of room. */}
          <div className="hidden min-w-0 flex-[1.4] large-desktop:block [&_img]:size-7 [&_section]:flex-wrap [&_section]:gap-1 [&_section]:overflow-visible [&_section]:pr-0">
            {hasPlayStyles && (
              <PlayStyles
                playStyles={playStyles}
                playStylesPlus={playStylesPlus}
                playStylesBonus={evolutionBonuses?.playStyles}
                playStylesPlusBonus={evolutionBonuses?.playStylesPlusBonus}
              />
            )}
          </div>
          <FaceStats
            attributes={attributes}
            upgrades={evolutionBonuses?.attributes}
            isGk={preferredPosition === "GK"}
          />
          <div className="ml-4 flex gap-x-4">
            <Stars
              label={t("chips.sm")}
              value={skillMoves}
              upgraded={evolutionBonuses?.skillMovesBonus}
            />
            <Stars
              label={t("chips.wf")}
              value={weakFoot}
              upgraded={evolutionBonuses?.weakFootBonus}
            />
          </div>
          <div className="flex w-44 flex-col items-center gap-y-1">
            {role}
            {chip}
          </div>
          <div className="flex w-20 shrink-0 items-center justify-end gap-x-2 pr-2">
            {price}
          </div>
        </div>
        {evoRow && <div className="-mb-1.5 mt-2">{evoRow}</div>}
      </div>
    </Card>
  );
};
